import { tenantApi } from '@shared/api/tenantApi';
import { unwrap } from '@shared/api/unwrap';

/**
 * Gallery — `GET /gallery/albums*` (`requireModule('gallery')`).
 * Responses use `{ success, albums }` or `{ success, album }` (not always nested in `data`).
 */

export interface GalleryAlbumSummary {
  album_id: string;
  name: string;
  cover_image_url?: string;
  photo_count: number;
  updated_at: string;
  album_date?: string;
}

export interface GalleryPhoto {
  photo_id: string;
  image_url: string;
  thumbnail_url?: string;
  caption?: string;
  taken_at?: string;
}

export interface GalleryAlbum extends GalleryAlbumSummary {
  description?: string;
  photos: GalleryPhoto[];
}

interface ApiAlbumRow {
  album_id: string;
  name: string;
  description?: string | null;
  album_date?: string;
  cover_url?: string | null;
  cover_image_url?: string;
  photo_count?: number;
  created_at?: string;
  updated_at?: string;
}

interface ApiImageRow {
  image_id: string;
  thumbnail_url?: string | null;
  original_filename?: string;
  created_at?: string;
}

interface ApiAlbumDetail extends ApiAlbumRow {
  images?: ApiImageRow[];
}

function mapAlbumSummary(row: ApiAlbumRow): GalleryAlbumSummary {
  const date = row.updated_at ?? row.created_at ?? row.album_date ?? '';
  return {
    album_id: row.album_id,
    name: row.name,
    cover_image_url: row.cover_url ?? row.cover_image_url ?? undefined,
    photo_count: Number(row.photo_count) || 0,
    updated_at: typeof date === 'string' ? date : String(date),
    album_date: row.album_date,
  };
}

function mapPhoto(row: ApiImageRow): GalleryPhoto {
  return {
    photo_id: row.image_id,
    image_url: row.thumbnail_url ?? '',
    thumbnail_url: row.thumbnail_url ?? undefined,
    caption: row.original_filename,
    taken_at: row.created_at,
  };
}

function albumsFromBody(body: unknown): ApiAlbumRow[] {
  if (!body || typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  if (Array.isArray(o.albums)) return o.albums as ApiAlbumRow[];
  const inner = unwrap<{ albums?: ApiAlbumRow[] }>(body);
  return Array.isArray(inner?.albums) ? inner.albums : [];
}

function albumFromBody(body: unknown): ApiAlbumDetail | null {
  if (!body || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;
  if (o.album && typeof o.album === 'object') return o.album as ApiAlbumDetail;
  const inner = unwrap<ApiAlbumDetail | { album?: ApiAlbumDetail }>(body);
  if (inner && typeof inner === 'object' && 'album_id' in inner) return inner as ApiAlbumDetail;
  if (inner && typeof inner === 'object' && 'album' in inner) return (inner as { album: ApiAlbumDetail }).album;
  return null;
}

export async function fetchGalleryAlbums(): Promise<GalleryAlbumSummary[]> {
  const { data } = await tenantApi.get('/gallery/albums', {
    params: { page: 1, limit: 100, all_locations: '1' },
  });
  return albumsFromBody(data).map(mapAlbumSummary);
}

export async function fetchGalleryAlbum(albumId: string): Promise<GalleryAlbum> {
  const { data } = await tenantApi.get(`/gallery/albums/${albumId}`, {
    params: { page: 1, limit: 200 },
  });
  const raw = albumFromBody(data);
  if (!raw) {
    throw new Error('Album not found');
  }
  const images = raw.images ?? [];
  return {
    ...mapAlbumSummary(raw),
    description: raw.description ?? undefined,
    photos: images.map(mapPhoto),
  };
}

/** Full-resolution presigned URL for lightbox viewing. */
export async function fetchGalleryImageUrl(albumId: string, imageId: string): Promise<string> {
  const { data } = await tenantApi.get(`/gallery/albums/${albumId}/images/${imageId}/url`);
  if (!data || typeof data !== 'object') return '';
  const o = data as Record<string, unknown>;
  if (typeof o.url === 'string') return o.url;
  const inner = unwrap<{ url?: string }>(data);
  return inner?.url ?? '';
}
